import {
    Box,
    Button,
    Card,
    Checkbox,
    Grow,
    IconButton,
    Link,
    Menu,
    MenuItem,
    TableCell,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import {
    DataGrid,
    GridCellParams,
    GridColDef,
    GridRowModel,
    GridRowSelectionModel
} from "@mui/x-data-grid";
import {
    Page,
    usePageNavigation
} from "../contexts/PageNavigationContextProvider";
import React, {
    useMemo,
    useState
} from "react";

import AddIcon from '@mui/icons-material/Add';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { CreateIssueButton } from "../issue/CreateIssueButton";
import { CreateLocationButton } from "../CreateLocationButton";
import { EditIcon } from "../../icons";
import { FieldDisplayData } from "../FieldDisplay";
import { Issue } from "../../../API";
import { IssueStatusDisplay } from "../IssueStatusDisplay";
import { KeyboardArrowDown } from "@mui/icons-material";
import { ProcureDimensionsModal } from "../dimensions/ProcureDimensionsModal";
import { QuickQuoteModal } from "../QuickQuoteModal";
import { ViewSwitcher } from "../ViewSwitcher";
import { formatDimension } from "../../../lib/dashboard/FormatDimension";
import { toFieldDisplayData } from "../../../lib/dashboard/StringUtils";
import { useIssueFilter } from "../contexts/IssueFilterContext";
import { useIssues } from "../contexts/IssuesContextProvider";
import { useLocations } from "../contexts/LocationContextProvider";

export const ListIssuesTable = () => {
    const {
        issues,
        issueIdToIssueMap,
        onEditIssueSubmit,
        parentIdToDimensionMap,
        submitQuickQuotes,
        isQuickQuoteSubmitting,
        onEditDimensionSubmit,
        submitIssue
    } = useIssues();
    const {
        locations,
        createLocation
    } = useLocations();
    const { navigateToPage } = usePageNavigation();
    const { updateFilter } = useIssueFilter();
    const isScreenSmallerThanSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showEditDimensions, setShowEditDimensions] = useState(false);
    const [issueIdToUpdate, setIssueIdToUpdate] = useState<string | null>(null);
    const [showQuickQuoteModal, setShowQuickQuoteModal] = useState(false);

    const onClickIssue = (issueId: string) => {
        updateFilter({ issueId });
        navigateToPage(Page.Issues, new URLSearchParams({ issueId }));
    };

    const handleProcessRowUpdate = (updatedRow: GridRowModel, previousRow: GridRowModel): GridRowModel => {
        if (onEditIssueSubmit) {
            const noteFieldDisplayProps: Array<FieldDisplayData<string>> = toFieldDisplayData(previousRow.note, "Description");

            // if description is changed, update the note
            if (updatedRow.description !== previousRow.description) {
                const newNoteField = [...noteFieldDisplayProps.filter(prop => prop.label !== "Description"), { label: "Description", data: updatedRow.description }];
                updatedRow.note = newNoteField.map(prop => `${prop.label}: ${prop.data}`).join("\n");
            }
            onEditIssueSubmit(updatedRow.id, updatedRow as Issue);
        }
        return updatedRow;
    };

    const handleEditClick = (event: React.MouseEvent, params: any) => {
        event.stopPropagation();
        event.preventDefault();
        params.api.startCellEditMode({ id: params.row.id, field: params.field });
    };

    // Handle dropdown menu
    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = (): void => {
        setAnchorEl(null);
    };

    const handleEditModalOpen = (params: GridCellParams, event: React.SyntheticEvent) => {
        if (params.field !== 'dimensions') {
            return null;
        }
        if (!onEditDimensionSubmit) {
            return null;
        }
        event.preventDefault();
        setIssueIdToUpdate(params.row.id);
        setShowEditDimensions(true);
    };

    const handleCloseModal = () => {
        setShowEditDimensions(false);
        setIssueIdToUpdate(null);
    };

    const getRowNonSelectableMessage = (issueId: string) => {
        if (isQuickQuoteSubmitting) {
            return "Please wait until the current Quick Estimate is complete.";
        }

        const issue = issueIdToIssueMap.get(issueId);
        if (!issue) {
            return "Issue not found. Please try again.";
        }

        if (issue.quickQuoteIds?.length) {
            return "A Quick Estimate is already in progress for this issue.";
        }
        return null;
    };

    const selectableRows = useMemo(() => {
        return issues.filter((issue) => {
            const nonSelectableMessage = getRowNonSelectableMessage(issue.id);
            if (nonSelectableMessage) {
                return false;
            }
            return true;
        });
    }, [issues]);

    const quickQuoteErrorMessage = selectedRows.reduce((acc, row) => {
        if (acc) {
            return acc;
        }
        if (isQuickQuoteSubmitting) {
            return "Quick Estimate is already in progress";
        }

        const issue = issueIdToIssueMap.get(row as string);
        if (!issue) {
            return "Error, not able to find issue";
        }

        if (issue.quickQuoteIds?.length) {
            return "Quick Estimate already started for one of the issues";
        }

        return "";
    }, "");

    const columns: GridColDef[] = [
        {
            field: "select",
            type: "actions",
            headerName: "",
            width: 50,
            renderHeader: () => (
                <Checkbox
                    sx={{ m: 0, p: 0 }}
                    checked={selectedRows.length === selectableRows.length}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < selectableRows.length}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedRows(selectableRows.map((issue) => issue.id));
                        } else {
                            setSelectedRows([]);
                        }
                    }}
                />
            ),
            renderCell: (params) => {
                const nonSelectableMessage = getRowNonSelectableMessage(params.row.id) || undefined;
                const isSelected = selectedRows.includes(params.row.id);
                return (
                    <Tooltip title={nonSelectableMessage} enterDelay={1000}>
                        <span>
                            <Checkbox
                                disabled={!!nonSelectableMessage}
                                checked={isSelected}
                                onChange={(e, checked) => {
                                    setSelectedRows(prev => {
                                        return [
                                            ...prev.filter(id => id !== params.row.id),
                                            checked ? params.row.id : undefined
                                        ].filter(Boolean);
                                    });
                                }}
                            />
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: "name",
            headerName: "Name",
            width: 250,
            editable: !!onEditIssueSubmit,
            renderCell: (params) => (
                <TableCell
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderBottom: "none",
                        "&:hover button": { opacity: 1 }, // show edit icon on hover
                        padding: 0,
                    }}
                >
                    <Link
                        color="info.main"
                        onClick={() => onClickIssue(params.row.id)}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': {
                                cursor: 'pointer'
                            }
                        }}
                    >
                        {params.value}
                    </Link>
                    {onEditIssueSubmit && (
                        <IconButton
                            size="small"
                            onClick={(e) => handleEditClick(e, params)}
                            sx={{
                                fontSize: 16,
                                ml: 1,
                                color: "#ffa726",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                position: "absolute",
                                right: "0px",
                                translateY: "-50%",
                                cursor: "pointer",
                                pointerEvents: "auto",
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </TableCell >
            )
        },
        {
            field: "description",
            headerName: "Description",
            minWidth: 200,
            flex: 1,
            editable: !!onEditIssueSubmit,
            valueGetter: (params) => {
                const noteFieldDisplayProps: Array<FieldDisplayData<string>> = toFieldDisplayData(params.row.note, "Description");
                return noteFieldDisplayProps.find(prop => prop.label === "Description")?.data || "";
            },
            renderCell: (params) => (
                <TableCell
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderBottom: "none",
                        padding: "0",
                        "&:hover button": { opacity: 1 }
                    }}
                >
                    {params.value}
                    {onEditIssueSubmit && (
                        <IconButton
                            size="small"
                            onClick={(e) => handleEditClick(e, params)}
                            sx={{
                                fontSize: 16,
                                ml: 1,
                                color: "#ffa726",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                position: "absolute",
                                right: "0px",
                                translateY: "-50%",
                                cursor: "pointer",
                                pointerEvents: "auto",
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </TableCell>
            )
        },
        {
            field: "submittedBy",
            headerName: "Submitted By",
            width: 170,
            valueGetter: (params) => {
                const noteFieldDisplayProps: Array<FieldDisplayData<string>> = toFieldDisplayData(params.row.note, "description");

                return noteFieldDisplayProps.find(prop => prop.label === "Submitted by")?.data || "";
            }
        },
        {
            field: "location",
            headerName: "Location",
            width: 150,
            valueGetter: (params) => {
                const location = locations.find((loc) => loc.id === params.row.locationId);
                return location ? location.name : "";
            }
        },
        {
            field: "status",
            headerName: "Status",
            width: 170,
            renderCell: (params) => {
                return (<IssueStatusDisplay status={params.value}></IssueStatusDisplay>);
            }
        },
        {
            field: "dimensions",
            headerName: "Dimensions",
            width: 170,
            editable: true,
            valueGetter: (params) => {
                const value = parentIdToDimensionMap.get(params.row.id);
                if (!value) {
                    return null;
                }
                const formattedDimension = formatDimension(value);
                if (!formattedDimension) {
                    return null;
                }
                return formattedDimension;
            },
            renderEditCell: (params) => {
                return (
                    <TableCell
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            borderBottom: "none",
                            "&:hover button": { opacity: 1 },
                            padding: "0"
                        }}
                    >
                        {params.value}
                    </TableCell>
                );
            },
            renderCell: (params) => (
                <TableCell
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderBottom: "none",
                        "&:hover button": { opacity: 1 },
                        padding: "0"
                    }}
                >
                    {params.value}
                    {onEditDimensionSubmit && (
                        <IconButton
                            size="small"
                            onClick={(e) => handleEditModalOpen(params, e)}
                            sx={{
                                fontSize: 16,
                                ml: 1,
                                color: "#ffa726",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                position: "absolute",
                                translateX: "-50%",
                                right: "0px",
                                cursor: "pointer",
                                pointerEvents: "auto",
                            }}
                            className="edit-dimensions"
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </TableCell>
            )
        },
        {
            field: "reportedDate",
            headerName: "Reported",
            width: 120,
            type: "date",
            valueGetter: (params) => {
                if (params.row.clientCreationDate) {
                    return params.row.clientCreationDate;
                }
                return params.row.createdAt;
            },
            valueFormatter: (params) => new Date(params.value as string).toLocaleDateString()
        },
    ];

    const bulkActionsHelperText = selectedRows.length === 0 ? "Select issues to enable" : "";

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Box display="flex" justifyContent="end" gap={1} flexWrap="wrap">
                {submitQuickQuotes && (
                    <Grow in={selectedRows.length > 0}>
                        <Box display="flex" justifyContent="end" alignItems="center">
                            <Tooltip title={bulkActionsHelperText} arrow>
                                <span>
                                    <Button
                                        color="inherit"
                                        onClick={handleMenuClick}
                                        disabled={selectedRows.length === 0}
                                        variant="outlined"
                                        endIcon={<KeyboardArrowDown fontSize="small" />}
                                        aria-label="bulk actions"
                                        sx={{
                                            textTransform: "none"
                                        }}
                                    >
                                        Bulk Actions
                                    </Button>
                                </span>
                            </Tooltip>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                                <Tooltip title={quickQuoteErrorMessage}>
                                    <span>
                                        <MenuItem
                                            disabled={!!quickQuoteErrorMessage}
                                            onClick={() => setShowQuickQuoteModal(true)}
                                        >
                                            <Typography variant="body2">
                                                Quick Estimate
                                            </Typography>
                                        </MenuItem>
                                    </span>
                                </Tooltip>
                            </Menu>
                        </Box>
                    </Grow>
                )}
                {createLocation && (
                    <CreateLocationButton
                        createLocation={createLocation}
                        icon={<AddLocationAltIcon />}
                        buttonProps={{
                            color: "inherit",
                            variant: "outlined",
                            sx: { textTransform: "none", alignSelf: "flex-end" },
                        }}
                        text={isScreenSmallerThanSm ? "Location" : "Add Location"}
                    />
                )}
                {submitIssue &&
                    <CreateIssueButton
                        icon={<AddIcon />}
                        buttonProps={{
                            color: "inherit",
                            variant: "outlined",
                            sx: { textTransform: "none", alignSelf: "flex-end" },
                        }}
                        text={isScreenSmallerThanSm ? "Issue" : "Add Issue"}
                    />
                }
                <ViewSwitcher />
            </Box>
            <Card>
                <DataGrid
                    rows={issues}
                    columns={columns}
                    onCellEditStart={(params, event) => handleEditModalOpen(params, event)}
                    processRowUpdate={handleProcessRowUpdate}
                    pageSizeOptions={[10, 20, 50]}
                    autoHeight
                    paginationMode="client"
                    pagination
                    editMode="cell"
                    initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                        sorting: {
                            sortModel: [{ field: "reportedDate", sort: "desc" }],
                        }
                    }}
                    sx={{
                        "& .MuiDataGrid-columnHeaders": {
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#DCDCDC",
                            fontWeight: "bold",
                            fontSize: "1rem",
                            zIndex: 2
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: "#EDEDED" // Zebra striping
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#e3f2fd" // Light blue on hover
                        },
                    }}
                    hideFooterSelectedRowCount
                />
            </Card>
            {showEditDimensions && (
                <ProcureDimensionsModal
                    showModal={showEditDimensions}
                    handleClose={handleCloseModal}
                    issueId={issueIdToUpdate || ""}
                />
            )}
            {showQuickQuoteModal && (
                <QuickQuoteModal
                    open={showQuickQuoteModal}
                    onClose={() => {
                        setAnchorEl(null);
                        setSelectedRows([]);
                        setShowQuickQuoteModal(false);
                    }}
                    issueIds={selectedRows as string[]}
                />
            )}
        </Box >
    );
};